export type BillingStatus =
  | "free"
  | "trialing"
  | "active"
  | "past_due"
  | "unpaid"
  | "canceled"
  | "incomplete"
  | "incomplete_expired"
  | "paused";

type BillingRecord = {
  billing_status?: string | null;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  billing_active_price_id_key?: string | null;
};

const KNOWN_BILLING_STATUSES = new Set<BillingStatus>([
  "free",
  "trialing",
  "active",
  "past_due",
  "unpaid",
  "canceled",
  "incomplete",
  "incomplete_expired",
  "paused",
]);

const ACTIVE_BILLING_STATUSES = new Set<BillingStatus>(["trialing", "active"]);

export function normalizeBillingStatus(status: string | null | undefined): BillingStatus {
  const normalized = String(status || "free").toLowerCase() as BillingStatus;
  return KNOWN_BILLING_STATUSES.has(normalized) ? normalized : "free";
}

export function hasBillingHistory(record: BillingRecord | null | undefined): boolean {
  if (!record) return false;

  const normalizedStatus = normalizeBillingStatus(record.billing_status);
  return Boolean(
    record.stripe_subscription_id
      || record.billing_active_price_id_key
      || normalizedStatus !== "free",
  );
}

export function hasActiveBillingAccess(record: BillingRecord | null | undefined): boolean {
  if (!hasBillingHistory(record)) return true;
  return ACTIVE_BILLING_STATUSES.has(normalizeBillingStatus(record?.billing_status));
}

export function getBillingStatusLabel(status: string | null | undefined): string {
  switch (normalizeBillingStatus(status)) {
    case "trialing":
      return "Trial active";
    case "active":
      return "Active";
    case "past_due":
      return "Payment past due";
    case "unpaid":
      return "Payment failed";
    case "canceled":
      return "Canceled";
    case "incomplete":
      return "Checkout incomplete";
    case "incomplete_expired":
      return "Checkout expired";
    case "paused":
      return "Paused";
    case "free":
    default:
      return "Free";
  }
}
